/**
 * Adapter hook that provides a unified view of collections for the library grid.
 *
 * When IS_UNIFIED_COLLECTIONS_ENABLED():
 *   - Canonical list comes from useCollections() (includes migrated web collections)
 *   - Legacy list comes from useUnmigratedSourceLists() (shown in separate section)
 *   - No name+owner merge heuristic — the backend is the source of truth
 *
 * When flag is OFF:
 *   - Returns stable empty arrays (no-op) — callers should not use this hook's output
 */
import { useMemo } from 'react';

import type {
  CollectionResponse,
  LibraryGridItem,
  SourceList,
  UnifiedCollection,
} from '../../../contracts';
import { isUnifiedCollectionsEnabled } from '../../injected';
import { useUnmigratedSourceLists } from '../../query/sourceListQueries';
import { useCollections } from './useCollections';

/**
 * Convert a CollectionResponse to a UnifiedCollection.
 * Sets _source based on whether the collection has domains (migrated web collection).
 */
function fromCollection(c: CollectionResponse): UnifiedCollection {
  const hasDomains = (c.domain_count ?? 0) > 0;
  return {
    id: c.collection_id,
    name: c.name,
    description: c.description,
    icon: c.icon ?? null,
    owner_id: c.owner_id,
    team_id: c.team_id,
    document_count: c.document_count,
    domains: c.domains ?? [],
    created_at: c.created_at,
    updated_at: c.updated_at,
    _source: hasDomains ? 'web' : 'library',
    kind: c.kind,
    _sourceListId: c.legacy_source_list_id ?? undefined,
  };
}

/**
 * Convert an unmigrated SourceList to a UnifiedCollection.
 * Always _source: 'web' since source lists are web-only.
 */
function fromSourceList(s: SourceList): UnifiedCollection {
  return {
    id: s.id,
    name: s.name,
    description: null,
    icon: null,
    owner_id: s.user_id,
    team_id: s.team_id,
    document_count: 0,
    domains: s.domains,
    created_at: s.created_at,
    updated_at: s.updated_at,
    _source: 'web',
  };
}

// ===== Main hook =====

/**
 * Empty return value for when the hook is called with the flag OFF.
 * Avoids running unified-specific queries while keeping the hook callable.
 */
const EMPTY_COLLECTIONS: UnifiedCollection[] = [];
const EMPTY_GRID_ITEMS: LibraryGridItem[] = [];

export function useUnifiedCollections() {
  const isUnifiedEnabled = isUnifiedCollectionsEnabled();
  const { data: collectionsData, isLoading: collectionsLoading } = useCollections();
  // Query is internally gated by enabled: isUnifiedEnabled
  const { data: unmigratedSourceLists, isLoading: unmigratedLoading } =
    useUnmigratedSourceLists();

  // When flag is OFF, skip all unified computation — callers should not
  // use this hook's output in the legacy render path.
  const collections = useMemo(() => {
    if (!isUnifiedEnabled) return EMPTY_COLLECTIONS;

    // Backend-aware: collections are canonical, no merge heuristic
    return (collectionsData?.collections ?? [])
      .map(fromCollection)
      .sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
  }, [isUnifiedEnabled, collectionsData]);

  /**
   * Flat list of all collections including unmigrated source lists,
   * converted to UnifiedCollection. Used by consumers that need a single
   * unified list (e.g. source control submenu).
   */
  const allCollections = useMemo(() => {
    if (!isUnifiedEnabled) return EMPTY_COLLECTIONS;

    const legacy = (unmigratedSourceLists ?? []).map(fromSourceList);
    return [...collections, ...legacy].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  }, [isUnifiedEnabled, collections, unmigratedSourceLists]);

  /**
   * Combined grid items: canonical collections + unmigrated legacy source lists.
   * Both render with the same visual card, but legacy items use migrate-first actions.
   * Sorted together by updated_at so legacy items don't cluster at the end.
   */
  const gridItems = useMemo((): LibraryGridItem[] => {
    if (!isUnifiedEnabled) return EMPTY_GRID_ITEMS;

    const canonical: LibraryGridItem[] = collections.map((c) => ({
      kind: 'collection' as const,
      item: c,
    }));
    const legacy: LibraryGridItem[] = (unmigratedSourceLists ?? []).map((s) => ({
      kind: 'legacy-source-list' as const,
      item: s,
    }));

    return [...canonical, ...legacy].sort((a, b) => {
      const aTime = new Date(a.item.updated_at).getTime();
      const bTime = new Date(b.item.updated_at).getTime();
      return bTime - aTime;
    });
  }, [isUnifiedEnabled, collections, unmigratedSourceLists]);

  const isLoading = isUnifiedEnabled ? collectionsLoading || unmigratedLoading : false;

  return { collections, allCollections, gridItems, isLoading };
}

/**
 * Look up whether a unified collection ID belongs to a library (document)
 * or web (source list) collection. Used by useEvidenceSourceConfig to split
 * unified IDs back into the two separate arrays the backend expects.
 *
 * In unified mode, all IDs are collection IDs — this function still works
 * because _source is set based on domain_count.
 */
export function splitUnifiedIds(
  ids: string[],
  collections: UnifiedCollection[]
): { libraryIds: string[]; webIds: string[] } {
  const sourceMap = new Map(collections.map((c) => [c.id, c._source]));
  const libraryIds: string[] = [];
  const webIds: string[] = [];

  for (const id of ids) {
    const source = sourceMap.get(id);
    if (source === 'web') {
      webIds.push(id);
    } else {
      libraryIds.push(id);
    }
  }

  return { libraryIds, webIds };
}
